import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Loader2 } from "lucide-react";

const ICON_CHOICES = ["MessagesSquare", "ArrowRightLeft", "RotateCw", "Gauge", "Signpost", "Merge", "Car", "ScrollText", "Siren", "BookOpen", "AlertTriangle", "Compass"];

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/admin/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const setField = (code, k, v) => setCats(cats.map((c) => c.code === code ? { ...c, [k]: v } : c));

  const save = async (c) => {
    setSaving(c.code);
    try {
      await api.patch(`/admin/categories/${c.code}`, { name_pt: c.name_pt, name_en: c.name_en, name_ur: c.name_ur, icon: c.icon });
      toast.success(`${c.code} saved`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(null); }
  };

  if (!cats.length) return <AdminLayout title="Categories"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></AdminLayout>;

  return (
    <AdminLayout title="Categories">
      <div className="space-y-3">
        {cats.map((c) => {
          const Icon = Icons[c.icon] || Icons.BookOpen;
          return (
            <div key={c.code} className="bg-white/4 border border-white/10 rounded-lg p-5" data-testid={`cat-card-${c.code}`}>
              <div className="grid lg:grid-cols-6 gap-4 items-start">
                <div className="lg:col-span-1 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-md bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                  <div>
                    <div className="font-mono text-xs text-white/50">{c.code}</div>
                    <div className="font-mono text-xs text-white/70">{c.question_count} q</div>
                  </div>
                </div>
                <div className="lg:col-span-4 grid grid-cols-3 gap-3">
                  <div><label className="text-[10px] font-mono uppercase text-white/50">🇵🇹 PT</label><Input value={c.name_pt} onChange={(e) => setField(c.code, "name_pt", e.target.value)} className="bg-white/5 border-white/15 text-white mt-1" data-testid={`name-pt-${c.code}`} /></div>
                  <div><label className="text-[10px] font-mono uppercase text-white/50">🇬🇧 EN</label><Input value={c.name_en || ""} onChange={(e) => setField(c.code, "name_en", e.target.value)} className="bg-white/5 border-white/15 text-white mt-1" data-testid={`name-en-${c.code}`} /></div>
                  <div><label className="text-[10px] font-mono uppercase text-white/50">🇵🇰 UR</label><Input value={c.name_ur || ""} onChange={(e) => setField(c.code, "name_ur", e.target.value)} className="bg-white/5 border-white/15 text-white mt-1" data-testid={`name-ur-${c.code}`} /></div>
                </div>
                <div className="lg:col-span-1">
                  <label className="text-[10px] font-mono uppercase text-white/50 mb-1 block">Icon</label>
                  <select value={c.icon} onChange={(e) => setField(c.code, "icon", e.target.value)} className="bg-white/5 border border-white/15 text-white h-9 w-full rounded-md text-sm px-2" data-testid={`icon-${c.code}`}>
                    {ICON_CHOICES.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <Button size="sm" onClick={() => save(c)} disabled={saving === c.code} className="w-full mt-2 bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90" data-testid={`save-cat-${c.code}`}>
                    {saving === c.code ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}
